import type { ImageCrop } from "@pax/api-contract/profiles/v1alpha1";

export type Crop = Required<ImageCrop>;

export const MAX_CROP_ZOOM = 5;
export const CENTRED_CROP: Crop = { x: 0.5, y: 0.5, zoom: 1 };

/** A picked image the browser can show, with its width over its height once upright. */
export interface LocalImage {
  url: string;
  aspect: number;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * The image's size in a frame with `frameAspect` (width over height), in frame widths and
 * heights. This is the media processor's crop: the largest region of the frame's shape that
 * fits in the image, divided by the zoom.
 */
function imageScale(zoom: number, imageAspect: number, frameAspect: number) {
  return {
    width: zoom * Math.max(1, imageAspect / frameAspect),
    height: zoom * Math.max(1, frameAspect / imageAspect),
  };
}

/** Keeps the crop inside the image, the way the media processor moves one near an edge. */
export function clampedCrop(crop: Crop, imageAspect: number, frameAspect: number): Crop {
  const zoom = clamp(crop.zoom, 1, MAX_CROP_ZOOM);
  const scale = imageScale(zoom, imageAspect, frameAspect);
  const halfWidth = 1 / (2 * scale.width);
  const halfHeight = 1 / (2 * scale.height);
  return {
    x: clamp(crop.x, halfWidth, 1 - halfWidth),
    y: clamp(crop.y, halfHeight, 1 - halfHeight),
    zoom,
  };
}

/** Where the image sits in its frame to show the crop, as percentages of the frame. */
export function cropPlacement(crop: Crop, imageAspect: number, frameAspect: number) {
  const kept = clampedCrop(crop, imageAspect, frameAspect);
  const scale = imageScale(kept.zoom, imageAspect, frameAspect);
  return {
    width: `${scale.width * 100}%`,
    height: `${scale.height * 100}%`,
    left: `${-(kept.x * scale.width - 0.5) * 100}%`,
    top: `${-(kept.y * scale.height - 0.5) * 100}%`,
  };
}

/** The crop after dragging the image by a fraction of the frame's width and height. */
export function draggedCrop(
  crop: Crop,
  imageAspect: number,
  frameAspect: number,
  across: number,
  down: number,
): Crop {
  const scale = imageScale(crop.zoom, imageAspect, frameAspect);
  return clampedCrop(
    { ...crop, x: crop.x - across / scale.width, y: crop.y - down / scale.height },
    imageAspect,
    frameAspect,
  );
}

/** Reads a picked file's upright shape, or null when the browser can't open it. */
export async function openLocalImage(file: File): Promise<LocalImage | null> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;
  try {
    await image.decode();
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
  if (image.naturalWidth === 0 || image.naturalHeight === 0) {
    URL.revokeObjectURL(url);
    return null;
  }
  return { url, aspect: image.naturalWidth / image.naturalHeight };
}

/** The crop to send, rounded so the request carries no float noise. */
export function requestCrop(crop: Crop): Crop {
  const round = (value: number) => Math.round(value * 10_000) / 10_000;
  return { x: round(crop.x), y: round(crop.y), zoom: round(crop.zoom) };
}
